import { useMemo, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  useHover,
  useClick,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import { useIsSmallScreen, viewportClamp } from "../lib/floating";
import {
  MapPin,
  UserCircle,
  Heart,
  Cake,
  Check,
  History,
  Edit2,
  MessageCircle,
  Star,
  ExternalLink,
  Flag,
  UserX,
  ArrowRightLeft,
  Mail,
  ArrowLeft,
  ArrowRight,
  UserPlus,
  AlertTriangle,
  StickyNote,
} from "lucide-react";
import { Link } from "wouter";
import { calculateAge, formatArray, formatValue } from "../utils/userUtils";
import { UserNotesSection } from "./UserNotesSection";
import { categorizeUser } from "../utils/userCategorizer";
import { explainCompatScore } from "../utils/matchFit";
import { AdminAPI } from "@shared/types/api";
import { ROLE_CONFIG } from "./roleConfig";
import { RoleBadge } from "./RoleBadge";

export interface AssignableUser {
  id: string;
  name: string;
  email: string;
  neighborhood?: string;
  score?: number;
  quizAnswers?: any;
  specialRequests?: string;
  assignmentReasoning?: string | null;
  confirmedAt?: string | Date | null;
  bookedAt?: string | Date | null;
  assignmentId?: string;
  flaggedForReview?: boolean;
  cantBeSeated?: boolean;
  /** Optional staff role (admin/moderator/analytics/tester), if any. */
  role?: AdminAPI.UserRole | null;
  /**
   * Accepted "+1" partners for this user at the current event — populated
   * from `acceptedInvites` on the admin event details payload. Pending or
   * cancelled invites are intentionally not surfaced.
   */
  plusOnePartners?: Array<{ userId: string; name: string }>;
  /**
   * Internal staff notes on this user (from the admin event details payload).
   * Shown only in admin surfaces — never to the user themself.
   */
  adminNotes?: AdminAPI.UserNote[];
}

function formatBookedAt(bookedAt: string | Date): string {
  const bookedDate = new Date(bookedAt);
  const diffMs = Date.now() - bookedDate.getTime();
  const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;
  if (diffMs >= 0 && diffMs < FORTY_EIGHT_HOURS_MS) {
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }
  return bookedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

interface DraggableUserProps {
  user: AssignableUser;
  restaurantNeighborhoodName?: string;
  /** Other users currently on the same table (excluding `user`). */
  tableMates?: AssignableUser[];
  /** True when this card is rendered inside a table (i.e. the user is seated). */
  isAssignedToTable?: boolean;
  isTableLocked?: boolean;
  /** Other users currently on the same table (excluding `user`). Used to flag repeat tablemates in History. */
  currentTableMateIds?: ReadonlySet<string>;
  history?: AdminAPI.UserHistory;
  onEdit?: (userId: string) => void;
  onToggleReview?: (userId: string) => void;
  /** Flag this guest as a no-show for the event (opens a no-show case). */
  onFlagNoShow?: (userId: string) => void;
  onReassign?: (userId: string) => void;
  onSendEmail?: (userId: string) => void;
  onNeighborhoodClick?: (neighborhood: string) => void;
}

const quizCategories = [
  {
    title: "Vibe",
    fields: [
      { label: "Dinner Vibe", key: "dinnerVibe" },
      { label: "Group Role", key: "groupRole" },
      { label: "Communication Style", key: "communicationStyle" },
      { label: "Group Chat Role", key: "groupChatRole" },
      { label: "Humor Style", key: "humorStyle" },
    ],
  },
  {
    title: "About You",
    fields: [
      { label: "Career Stage", key: "careerStage" },
      { label: "Industry", key: "industry" },
      { label: "Unwind Activities", key: "unwindActivities" },
      { label: "Weekend Energy", key: "weekendEnergy" },
      { label: "Conversation Topics", key: "conversationTopics" },
      { label: "Hobbies", key: "hobbies" },
      { label: "Social Battery", key: "socialBattery" },
      { label: "Relationship Status", key: "relationshipStatus" },
      { label: "Gender Identity", key: "genderIdentity" },
      { label: "Pronouns", key: "pronouns" },
      { label: "Sexual Orientation", key: "sexualOrientation" },
      { label: "Age", key: "birthday" },
      { label: "Community", key: "community" },
      { label: "Age Preferences", key: "ageRangePreferences" },
    ],
  },
  {
    title: "Values",
    fields: [
      { label: "Primary Value 1", key: "primaryValue1" },
      { label: "Primary Value 2", key: "primaryValue2" },
      { label: "Conflict Style", key: "conflictStyle" },
      { label: "Feedback Style", key: "feedbackStyle" },
      { label: "Community Preference", key: "communityPreference" },
      { label: "Political Views", key: "politicalViews" },
      { label: "Topics Open To", key: "topicsOpenTo" },
    ],
  },
];

function parseSpecialRequests(specialRequests?: string) {
  if (!specialRequests) return null;
  try {
    return JSON.parse(specialRequests);
  } catch {
    return null;
  }
}

export function DraggableUser({
  user,
  restaurantNeighborhoodName,
  tableMates,
  isAssignedToTable,
  currentTableMateIds,
  history,
  onEdit,
  onToggleReview,
  onFlagNoShow,
  onReassign,
  onSendEmail,
  onNeighborhoodClick,
}: DraggableUserProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useSortable({ id: user.id });

  // No sort transition: admin tool, instant reflow beats a slide animation.
  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const quizAnswers = user.quizAnswers || {};
  const genderIdentity = quizAnswers.genderIdentity || [];
  const sexualOrientation = quizAnswers.sexualOrientation || [];
  const birthday = quizAnswers.birthday;
  const age = calculateAge(birthday);

  const genderDisplay = formatArray(genderIdentity);
  const orientationDisplay = formatArray(sexualOrientation);
  const community: string | undefined = quizAnswers.community;
  const communityDisplay = community
    ? community.charAt(0).toUpperCase() + community.slice(1)
    : "";
  const ageRangePreferences: string[] = Array.isArray(
    quizAnswers.ageRangePreferences,
  )
    ? quizAnswers.ageRangePreferences
    : [];
  const ageRangePreferencesDisplay = formatArray(ageRangePreferences);

  const specialRequests = parseSpecialRequests(user.specialRequests);
  const hasSpecialRequests = specialRequests;

  const userCategory = useMemo(
    () => categorizeUser(user, { specialRequests: user.specialRequests }),
    [user],
  );
  const compatBreakdown = useMemo(
    () => explainCompatScore(user, tableMates || []),
    [user, tableMates],
  );
  const compatScore = compatBreakdown.score;
  const compatPct = Math.round(compatScore * 100);
  const compatBg =
    compatScore < 0.5
      ? "bg-red-500"
      : compatScore < 0.8
        ? "bg-amber-400"
        : "bg-emerald-500";
  const compatTitle = [
    `Compat: ${compatPct}% (age + gender)`,
    `Gender ${Math.round(compatBreakdown.gender.score * 100)}%: ${compatBreakdown.gender.label}`,
    `Age ${Math.round(compatBreakdown.age.score * 100)}%: ${compatBreakdown.age.label}`,
  ].join("\n");
  const hasHistory =
    history &&
    (history.previousRestaurants.length > 0 ||
      history.previousTableMates.length > 0);
  const hasFeedback =
    history?.recentFeedback && history.recentFeedback.length > 0;

  const assignmentReasoning = useMemo<
    { compatibilityScore?: number; reasons?: string[] } | undefined
  >(() => {
    if (!user.assignmentReasoning) return null;
    try {
      return JSON.parse(user.assignmentReasoning);
    } catch {
      return { compatibilityScore: 0, reasons: [user.assignmentReasoning] };
    }
  }, [user.assignmentReasoning]);

  // Floating UI setup for tooltip
  const [isOpen, setIsOpen] = useState(false);
  const isSmallScreen = useIsSmallScreen();

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: isSmallScreen
      ? [] // the bottom sheet is CSS-positioned; anchored middleware would fight it
      : [
          offset(8),
          flip({
            // Prefer above/below over the opposite side: on a narrow-but-not-phone
            // viewport neither side has room for the 320px card, and a squeezed
            // side placement reads as half off-screen.
            fallbackPlacements: ["bottom-start", "top-start", "left-start"],
          }),
          ...viewportClamp(320),
        ],
    whileElementsMounted: autoUpdate,
    placement: "right-start",
  });

  const hover = useHover(context, {
    enabled: !isDragging && !isSmallScreen,
    delay: { open: 200, close: 300 },
    move: false,
  });
  // Touch has no hover; tap opens the sheet instead. Drag still wins because
  // the pointer handlers are stripped from the reference props below.
  const click = useClick(context, { enabled: isSmallScreen && !isDragging });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: isSmallScreen ? "dialog" : "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
    role,
  ]);

  // Merge refs for sortable and floating UI
  const mergedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    refs.setReference(node);
  };

  // Get floating UI props but filter out event handlers that conflict with drag
  const floatingProps = getReferenceProps();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onPointerDown, onPointerUp, ...safeFloatingProps } =
    floatingProps as any;

  // Check if user's selected neighborhood doesn't match the restaurant's neighborhood
  const isNeighborhoodMismatch =
    user.neighborhood &&
    restaurantNeighborhoodName &&
    user.neighborhood.toLowerCase() !==
      restaurantNeighborhoodName.toLowerCase();

  return (
    <>
      <div
        ref={mergedRef}
        style={style}
        {...attributes}
        {...listeners}
        {...safeFloatingProps}
        className={`relative overflow-hidden p-3 pl-4 rounded-lg border border-content transition-colors cursor-move ${
          user.cantBeSeated
            ? "bg-amber-500/10 border-amber-500/40 hover:border-amber-500"
            : isNeighborhoodMismatch
              ? "bg-red-400/80 border-red-500 hover:border-red-400"
              : "bg-dark-elevated border-dark-border hover:border-primary-500"
        }`}
      >
        {isAssignedToTable && user.role && (
          <div
            className="mb-2 flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-1 text-[11px] font-medium text-amber-300"
            title={`This is a ${ROLE_CONFIG[user.role].label} account — it is assigned to a real dinner table. Verify this is intended.`}
          >
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>{ROLE_CONFIG[user.role].label} account seated at a table</span>
          </div>
        )}
        {compatScore < 1 && (
          <div
            className={`absolute left-0 top-0 bottom-0 w-1.5 ${compatBg}`}
            title={compatTitle}
          />
        )}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 flex-wrap">
              <p className="font-medium text-sm truncate">{user.name}</p>
              {user.role && <RoleBadge role={user.role} className="w-3.5 h-3.5" />}
              {user.cantBeSeated && (
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 shrink-0"
                  title="This user has been marked can't be seated"
                >
                  Can't be seated
                </span>
              )}
              {user.confirmedAt && (
                <Check
                  className="w-4 h-4 p-0.5 bg-green-500 text-white rounded-full shrink-0"
                  aria-label="Confirmed"
                />
              )}
              {hasHistory && (
                <History
                  className="w-3.5 h-3.5 text-purple-500 shrink-0"
                  aria-label="Has previous history"
                />
              )}
              {hasFeedback && (
                <MessageCircle
                  className="w-3.5 h-3.5 text-blue-500 shrink-0"
                  aria-label="Has feedback"
                />
              )}
              {user.adminNotes && user.adminNotes.length > 0 && (
                <StickyNote
                  className="w-3.5 h-3.5 text-amber-500 shrink-0"
                  aria-label="Has staff notes"
                />
              )}
            </div>
            <p className="text-xs text-dark-muted truncate">{user.email}</p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {user.neighborhood && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onNeighborhoodClick?.(user.neighborhood!);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-xs text-primary-800 hover:text-primary transition-colors cursor-pointer max-w-[8rem]"
                title={`Filter by ${user.neighborhood}`}
              >
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="capitalize truncate">{user.neighborhood}</span>
              </button>
            )}
            {user.plusOnePartners && user.plusOnePartners.length > 0 && (() => {
              // Amber when any partner isn't on this table (or this user is
              // currently unassigned and partner is on a table — handled by
              // the parent banner). Neutral when all partners are tablemates.
              const split = user.plusOnePartners.some(
                (p) => !currentTableMateIds?.has(p.userId),
              );
              const names = user.plusOnePartners.map((p) => p.name).join(", ");
              return (
                <span
                  className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    split
                      ? "bg-amber-500/15 text-amber-400"
                      : "bg-primary-500/15 text-primary-400"
                  }`}
                  title={`+1 with ${names}${split ? " — not at this table" : ""}`}
                >
                  <UserPlus className="w-3 h-3" />
                  +1
                </span>
              );
            })()}
            <div className="flex items-center gap-1">
              <Link
                href={`/users?q=${user.id}`}
                title={`View user: ${user.name}`}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className="p-1 rounded hover:bg-base-content/10 text-base-content/60 hover:text-primary transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onEdit(user.id);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-1 rounded hover:bg-base-content/10 text-base-content/60 hover:text-primary transition-colors"
                  title="Edit reservation"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
              )}
              {onSendEmail && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSendEmail(user.id);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-1 rounded hover:bg-base-content/10 text-base-content/60 hover:text-primary transition-colors"
                  title="Send email"
                >
                  <Mail className="w-3.5 h-3.5" />
                </button>
              )}
              {onReassign && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onReassign(user.id);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-1 rounded hover:bg-base-content/10 text-base-content/60 hover:text-accent transition-colors"
                  title="Reassign to another table"
                >
                  <ArrowRightLeft className="w-3.5 h-3.5" />
                </button>
              )}
              {onToggleReview && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onToggleReview(user.id);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className={`p-1 rounded transition-colors ${user.flaggedForReview ? "text-warning hover:text-warning/70" : "text-base-content/30 hover:text-warning"}`}
                  title={
                    user.flaggedForReview
                      ? "Clear review flag"
                      : "Flag for review"
                  }
                >
                  <Flag
                    className="w-3.5 h-3.5"
                    fill={user.flaggedForReview ? "currentColor" : "none"}
                  />
                </button>
              )}
              {onFlagNoShow && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onFlagNoShow(user.id);
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-1 rounded transition-colors text-base-content/30 hover:text-error"
                  title="Flag as no-show"
                >
                  <UserX className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {genderDisplay && (
            <div className="flex items-center gap-1 text-dark-muted">
              <UserCircle className="w-3 h-3" />
              <span>{genderDisplay}</span>
            </div>
          )}
          {orientationDisplay && (
            <div className="flex items-center gap-1 text-dark-muted">
              <Heart className="w-3 h-3" />
              <span>{orientationDisplay}</span>
            </div>
          )}
          {age !== null && (
            <div className="flex items-center gap-1 text-dark-muted">
              <Cake className="w-3 h-3" />
              <span>{age}y</span>
            </div>
          )}
          {community && (
            <span
              className={`inline-flex items-center justify-center w-4 h-4 rounded-sm text-[9px] font-bold text-white ${
                community === "sapphic"
                  ? "bg-pink-600"
                  : community === "men"
                    ? "bg-blue-600"
                    : "bg-emerald-600"
              }`}
              title={`Community: ${communityDisplay}`}
              aria-label={`Community: ${communityDisplay}`}
            >
              {community === "sapphic"
                ? "S"
                : community === "men"
                  ? "M"
                  : "A"}
            </span>
          )}
          {ageRangePreferences.length > 0 && (
            <span
              className="inline-flex items-center gap-0.5 font-mono text-[11px] leading-none text-dark-muted"
              title={`Age preferences: ${ageRangePreferencesDisplay}`}
              aria-label={`Age preferences: ${ageRangePreferencesDisplay}`}
            >
              {ageRangePreferences.includes("younger") && <span>-</span>}
              {ageRangePreferences.includes("same") && <span>=</span>}
              {ageRangePreferences.includes("older") && <span>+</span>}
            </span>
          )}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
            {compatScore < 1 && (
              <span
                className={`text-[10px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm ${compatBg}`}
                title={compatTitle}
              >
                {compatPct}
              </span>
            )}
            <span
              className={`text-[10px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm ${
                userCategory.gender === "M"
                  ? "bg-blue-600"
                  : userCategory.gender === "F"
                    ? "bg-pink-600"
                    : "bg-purple-600"
              }`}
              title={`Category: ${userCategory.label}`}
            >
              {userCategory.label}
            </span>
          </div>
        </div>
        {/* Special Requests Section */}
        {hasSpecialRequests && (
          <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
            {Object.entries(specialRequests).map(([key, value]) => {
              if (
                value === null ||
                value === undefined ||
                value === "" ||
                value === false
              )
                return null;

              // Format the key to be human-readable
              const label = key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())
                .trim();

              // Handle different value types
              if (typeof value === "boolean" && value === true) {
                return (
                  <span
                    key={key}
                    className="bg-primary/20 text-primary-800 px-1.5 py-0.5 rounded"
                  >
                    {label}
                  </span>
                );
              }

              return (
                <span
                  key={key}
                  className="bg-base-200 text-type-900 dark:text-type-200 px-1.5 py-0.5 rounded"
                >
                  <span className="font-medium">{label}:</span>{" "}
                  {String(value)}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Tooltip using Floating UI Portal */}
      {isOpen && !isDragging && (
        <FloatingPortal>
          {isSmallScreen && (
            <div
              className="fixed inset-0 z-[9998] bg-black/50"
              onClick={() => setIsOpen(false)}
            />
          )}
          <div
            ref={refs.setFloating}
            style={isSmallScreen ? undefined : floatingStyles}
            {...getFloatingProps()}
            className={
              isSmallScreen
                ? "fixed inset-x-0 bottom-0 z-[9999] bg-base-100 rounded-t-2xl shadow-2xl border-t border-base-300 p-4 pt-2 max-h-[75vh] overflow-y-auto pointer-events-auto pb-[env(safe-area-inset-bottom)]"
                : "z-[9999] w-80 bg-base-100 rounded-lg shadow-2xl border border-base-300 p-4 overflow-y-auto pointer-events-auto"
            }
          >
            {isSmallScreen && (
              <div className="sticky top-0 z-10 -mt-2 mb-1 flex justify-center pt-2 pb-1 bg-base-100">
                <div className="w-9 h-1 rounded-full bg-base-300" />
              </div>
            )}
            <div style={isSmallScreen ? undefined : { maxWidth: 320, overflow: "auto" }}>
              {user.bookedAt && (
                <div className="text-[11px] text-base-content/60 mb-2">
                  Booked {formatBookedAt(user.bookedAt)}
                </div>
              )}
              {user.assignmentReasoning && (
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    marginBottom: "12px",
                  }}
                >
                  Compatibility Score: {assignmentReasoning?.compatibilityScore}
                  <br />
                  Reasons:
                  <br />
                  {assignmentReasoning?.reasons?.join(", ")}
                </div>
              )}

              <div className="mb-4 pb-4 border-b border-base-300">
                <UserNotesSection
                  userId={user.id}
                  notes={user.adminNotes || []}
                  collapsedWhenEmpty
                />
              </div>

              {/* User History Section */}
              {history && (
                <div className="mb-4 pb-4 border-b border-base-300">
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "bold",
                      marginBottom: "8px",
                    }}
                  >
                    History
                  </div>

                  {history.previousRestaurants.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-gray-500 mb-1">
                        Previous Restaurants:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {history.previousRestaurants.map((r) => (
                          <span
                            key={r.id}
                            className="text-[10px] bg-base-200 px-1.5 py-0.5 rounded border border-base-300"
                          >
                            {r.name}
                            <span className="text-gray-400 ml-1">
                              {new Date(r.visitedAt).toLocaleDateString(
                                "en-US",
                                { month: "short", day: "numeric" },
                              )}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {history.previousTableMates.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 mb-1">
                        Previous Tablemates:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Array.from(
                          history.previousTableMates
                            .reduce((acc, m) => {
                              const existing = acc.get(m.id);
                              if (existing) {
                                existing.count += 1;
                              } else {
                                acc.set(m.id, {
                                  id: m.id,
                                  name: m.name,
                                  connection: m.connection,
                                  count: 1,
                                });
                              }
                              return acc;
                            }, new Map<string, { id: string; name: string; connection: (typeof history.previousTableMates)[number]["connection"]; count: number }>())
                            .values(),
                        ).map((m) => {
                          const repeatWithCurrent =
                            currentTableMateIds?.has(m.id) ?? false;
                          const tooltipParts = [];
                          if (m.count > 1) {
                            tooltipParts.push(`Sat together ${m.count} times`);
                          }
                          if (repeatWithCurrent) {
                            tooltipParts.push(
                              "Also assigned to this table now",
                            );
                          }
                          if (m.connection === "mutual") {
                            tooltipParts.push(
                              "Mutually connected (both shared contact info)",
                            );
                          } else if (m.connection === "only_them") {
                            tooltipParts.push(
                              `Only ${m.name} shared contact info`,
                            );
                          } else if (m.connection === "only_me") {
                            tooltipParts.push(
                              `Only ${user.name} shared contact info`,
                            );
                          }
                          return (
                            <span
                              key={m.id}
                              title={
                                tooltipParts.length > 0
                                  ? tooltipParts.join(" • ")
                                  : undefined
                              }
                              className={
                                "inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border " +
                                (repeatWithCurrent
                                  ? "bg-red-600/35 border-red-500/70 text-red-50"
                                  : "bg-base-200 border-base-300 text-white")
                              }
                            >
                              {m.name}
                              {m.count > 1 && (
                                <span className="font-semibold text-amber-300">
                                  {m.count}x
                                </span>
                              )}
                              {m.connection === "mutual" && (
                                <ArrowRightLeft className="w-3 h-3 text-emerald-400 shrink-0" />
                              )}
                              {m.connection === "only_them" && (
                                <ArrowLeft className="w-3 h-3 text-sky-400 shrink-0" />
                              )}
                              {m.connection === "only_me" && (
                                <ArrowRight className="w-3 h-3 text-amber-400 shrink-0" />
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {history.previousRestaurants.length === 0 &&
                    history.previousTableMates.length === 0 && (
                      <div className="text-xs text-gray-400 italic">
                        No previous history found.
                      </div>
                    )}

                  {/* Recent Feedback Section */}
                  {history.recentFeedback &&
                    history.recentFeedback.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-base-300">
                        <div className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          Recent Feedback:
                        </div>
                        <div className="flex flex-col gap-2">
                          {history.recentFeedback.map((f) => (
                            <div
                              key={f.id}
                              className="text-[10px] bg-blue-50 dark:bg-blue-900/20 px-2 py-1.5 rounded border border-blue-200 dark:border-blue-800"
                            >
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2">
                                  {f.restaurantName && (
                                    <span className="font-medium text-blue-700 dark:text-blue-300">
                                      {f.restaurantName}
                                    </span>
                                  )}
                                  <span className="text-gray-400">
                                    {new Date(f.createdAt).toLocaleDateString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      },
                                    )}
                                  </span>
                                </div>
                                {f.overallExperience && (
                                  <span className="flex items-center gap-0.5 text-amber-600 dark:text-amber-400">
                                    <Star className="w-3 h-3 fill-current" />
                                    {f.overallExperience}/5
                                  </span>
                                )}
                              </div>
                              {f.notes && (
                                <p className="text-gray-600 dark:text-gray-400 italic">
                                  "{f.notes}"
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  marginBottom: "12px",
                }}
              >
                Quiz Answers
              </div>
              {quizCategories.map((category) => (
                <div key={category.title} style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#9333ea",
                      marginBottom: "8px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {category.title}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    {category.fields.map((field) => {
                      const value = quizAnswers[field.key];
                      let displayValue: string;

                      if (field.key === "birthday") {
                        displayValue = age ? `${age} years` : "N/A";
                      } else if (field.key === "politicalViews") {
                        displayValue = value ? `${value}/10` : "N/A";
                      } else {
                        displayValue = formatValue(value);
                      }

                      return (
                        <div
                          key={field.key}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "8px",
                            fontSize: "12px",
                          }}
                        >
                          <span
                            className={
                              "text-neutral-800 dark:text-neutral-200 font-medium"
                            }
                          >
                            {field.label}:
                          </span>
                          <span
                            className={
                              "text-secondary-700 dark:text-secondary-400 text-right flex-1"
                            }
                          >
                            {displayValue}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
